import { useState } from 'react'
import { X, Clock, Download } from 'lucide-react'
import HistoryTimeline from './HistoryTimeline'
import ModalPortal from './ModalPortal'
import ModalFrame from './ModalFrame'

const HistoryModal = ({ isOpen, onClose, talent, events }) => {
  const [isLoading, setIsLoading] = useState(false)

  if (!isOpen) return null

  const handleExportHistory = async () => {
    if (!events || events.length === 0) return
    
    setIsLoading(true)
    try {
      // Create CSV content
      const csvContent = [
        ['Date', 'Changed By', 'Event Type', 'Context', 'Details'],
        ...events.map(event => [
          new Date(event.occurred_at).toLocaleString('en-US'),
          event.user?.name || event.user_id || 'System',
          event.event_type || 'Unknown',
          event.business_context || '',
          event.event_data ? JSON.stringify(event.event_data) : ''
        ])
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `history_${talent?.first_name && talent?.last_name 
        ? `${talent.first_name}_${talent.last_name}`
        : talent?.name || 'unknown'}_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error exporting history:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ModalPortal onClose={onClose} ariaLabel="History">
        <ModalFrame size="lg" className="-m-3 w-[calc(100%+1.5rem)] rounded-none border border-light-border-primary dark:border-dark-border-primary sm:m-0 sm:w-full sm:rounded-lg">
          {/* Header */}
          <ModalFrame.Header className="items-center px-4 py-4 sm:px-6">
              <div className="flex min-w-0 items-center">
                <Clock className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">
                    Change History
                  </h3>
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                    {talent?.first_name && talent?.last_name 
                      ? `${talent.first_name} ${talent.last_name}`
                      : talent?.name || 'Unknown'}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {events && events.length > 0 && (
                  <button
                    onClick={handleExportHistory}
                    disabled={isLoading}
                    className="inline-flex min-h-10 items-center px-3 py-2 border border-light-border-primary dark:border-dark-border-primary shadow-sm text-sm leading-4 font-medium rounded-md text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {isLoading ? 'Exporting...' : 'Export CSV'}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-tertiary hover:text-light-text-secondary dark:hover:bg-dark-bg-tertiary dark:hover:text-dark-text-secondary transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
          </ModalFrame.Header>

          {/* Content */}
          <ModalFrame.Body className="bg-light-bg-primary px-4 py-4 dark:bg-dark-bg-secondary sm:px-6">
            {events && events.length > 0 ? (
              <HistoryTimeline events={events} maxEvents={events.length} />
            ) : (
              <div className="text-center py-8 text-light-text-tertiary dark:text-dark-text-tertiary">
                <Clock className="h-12 w-12 mx-auto mb-4 text-light-text-tertiary dark:text-dark-text-tertiary" />
                <p className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  No change history available
                </p>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  No changes have been recorded yet
                </p>
              </div>
            )}
          </ModalFrame.Body>

          {/* Footer */}
          <ModalFrame.Footer className="flex justify-end bg-light-bg-tertiary px-4 py-3 dark:bg-dark-bg-tertiary sm:px-6">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-light-border-primary dark:border-dark-border-primary shadow-sm text-sm font-medium rounded-md text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Close
            </button>
          </ModalFrame.Footer>
        </ModalFrame>
    </ModalPortal>
  )
}

export default HistoryModal
